import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import type { IntlService } from 'ember-intl'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { ReportingUiScopeSelect } from 'teacher-ui-client/components/reporting/ui-scope-select'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import type WritingLegendsSpellingRoute from 'teacher-ui-client/reporting/ui-scope/writing-legends/spelling/route'
import { UnderlineTabButton } from 'teacher-ui-client/components/underline-tab/button'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import type WritingLegendsSpellingController from 'teacher-ui-client/reporting/ui-scope/writing-legends/spelling/controller'
import { SpellingTab } from 'teacher-ui-client/reporting/ui-scope/writing-legends/spelling/controller'
import { ReportingControls } from 'teacher-ui-client/components/reporting/controls'
import { MyStudentsSpelling } from 'teacher-ui-client/components/writing-legends/my-students-spelling'
import { SpellingRule } from 'teacher-ui-client/components/writing-legends/spelling-rule'
import { SpellingRuleWords } from 'teacher-ui-client/components/writing-legends/spelling-rule-words'
import { SpellingIndividualWords } from 'teacher-ui-client/components/writing-legends/spelling-individual-words'
import { SpellingStudentWords } from 'teacher-ui-client/components/writing-legends/spelling-student-words'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import Tooltip from 'teacher-ui-client/components/tooltip'

interface Signature {
  Args: {
    model: ModelFor<WritingLegendsSpellingRoute>
    controller: WritingLegendsSpellingController
  }
}

export class WritingLegendsSpellingPage extends Component<Signature> {
  @service intl!: IntlService

  @service subscriptionType!: SubscriptionTypeService

  perPage = 10

  get themeKey() {
    return subscriptionTypeToThemeKey(this.subscriptionType.current)
  }

  get spellingTabs() {
    return Object.values(SpellingTab).map((tab) => {
      return {
        id: tab,
        name: this.intl.t(`pages.wlSpelling.tabs.${tab}`),
        selected: this.args.controller.tab === tab,
      }
    })
  }

  setTab = (tab: SpellingTab) => {
    this.args.controller.tab = tab

    // Using a common column for sorting across all tabs
    this.args.controller.sort = '-total_incorrect'
    this.args.controller.pageNumber = 1 // reset the page number on tab change
    this.args.controller.rule = ''
    this.args.controller.student = ''
  }

  <template>
    <div class="flex w-full flex-col">
      <PageHeader>
        <:title>
          <div class="flex print:hidden">
            <h1>
              {{t "reporting.pageTitles.writingLegends.spelling"}}
            </h1>
            <div class="ml-2">
              <PhosphorIcon class="text-juicy-orange-350" @icon="phosphor-info-fill" />
              <Tooltip @text={{t "pages.wlSpelling.titleTooltip"}} />
            </div>
          </div>
        </:title>
      </PageHeader>

      <ReportingControls>
        <ReportingUiScopeSelect class="shrink basis-80" />
        <ReportingPeriodSelect />
      </ReportingControls>

      <div class="mb-5 flex gap-2 print:hidden">
        {{#each this.spellingTabs as |spellingTab|}}
          <UnderlineTabButton
            @active={{spellingTab.selected}}
            class="grow"
            @themeKey={{this.themeKey}}
            {{on "click" (fn this.setTab spellingTab.id)}}
          >
            {{spellingTab.name}}
          </UnderlineTabButton>
        {{/each}}
      </div>

      {{#if (eq @controller.tab SpellingTab.MyStudents)}}
        {{#if @controller.student}}
          <SpellingStudentWords
            @period={{@model.period}}
            @uiScope={{@model.uiScope}}
            @students={{@model.students}}
            @pageNumber={{@controller.pageNumber}}
            @perPage={{this.perPage}}
          />
        {{else}}
          <MyStudentsSpelling
            @period={{@model.period}}
            @uiScope={{@model.uiScope}}
            @students={{@model.students}}
            @pageNumber={{@controller.pageNumber}}
            @perPage={{this.perPage}}
          />
        {{/if}}
      {{else if (eq @controller.tab SpellingTab.SpellingRule)}}
        {{#if @controller.rule}}
          <SpellingRuleWords
            @period={{@model.period}}
            @uiScope={{@model.uiScope}}
            @students={{@model.students}}
            @pageNumber={{@controller.pageNumber}}
            @perPage={{this.perPage}}
          />
        {{else}}
          <SpellingRule
            @period={{@model.period}}
            @uiScope={{@model.uiScope}}
            @students={{@model.students}}
            @pageNumber={{@controller.pageNumber}}
            @perPage={{this.perPage}}
          />
        {{/if}}
      {{else if (eq @controller.tab SpellingTab.IndividualWords)}}
        <SpellingIndividualWords
          @period={{@model.period}}
          @uiScope={{@model.uiScope}}
          @students={{@model.students}}
          @pageNumber={{@controller.pageNumber}}
          @perPage={{this.perPage}}
        />
      {{/if}}
    </div>
  </template>
}

export default RouteTemplate(WritingLegendsSpellingPage)
